// The pieces Konfiguration (and next Compliance) are built from, the same design language as Gennemgang:
//   TileMenu       the horizontal menu: tiles with an icon, a name and a status line, in a white frame
//   SectionHeading a coloured pill with its count and a short explanation under it; folds everything below it
//   StatusCard     one card of rows under a status (Skal rettes, Bør ses på, I orden ...), 4 rows and "Vis alle"
//   OnePagePanel   the side panel: header with a status dot, the coloured note, one expand button, folded cards, the decision pinned at the bottom
// Styles: StatusCards.css (cards and panel reuse .wqc* and .pn* from pages/WorkQueueCards.css).
import React, { useEffect, useState } from 'react';
import '../pages/WorkQueueCards.css';
import './StatusCards.css';
import ExpandAll from './ExpandAll';
import DecisionTag from './DecisionTag';
import { getStatusBadge } from '../utils/statusBadge';
import './ItListCard.css';

// "Udvid alle" in a side panel is remembered (in this browser): once pressed, every panel opens with all its cards open, until
// "Fold alle sammen" is pressed. Folding or opening a single card does not change it.
const EXPAND_KEY = 'nc-panel-expand-all';
export const readExpandAll = () => { try { return localStorage.getItem(EXPAND_KEY) === 'on'; } catch { return false; } };
export const writeExpandAll = (on) => { try { localStorage.setItem(EXPAND_KEY, on ? 'on' : 'off'); } catch { /* not remembered */ } };

const CHEVRON = (
  <svg className="wqc__cv" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
);
const CLIPBOARD = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 3h6v4H9z" /><rect x="5" y="5" width="14" height="16" rx="2" /><path d="M9 12h6M9 16h6" /></svg>
);

// tiles: [{ key, name, icon, bg, fg, status: { text, tone }, active, off, onClick }]; tone is bad, warn, ok or mute.
export function TileMenu({ tiles, label, className = '' }) {
  return (
    <div className={`tm${className ? ` ${className}` : ''}`} role="tablist" aria-label={label}>
      {tiles.map((t) => (
        <button key={t.key} type="button" role="tab" title={`${t.name}: ${t.status.text}`} aria-selected={t.active} className={`tm__t${t.active ? ' is-on' : ''}${t.off ? ' is-off' : ''}`} onClick={t.onClick}>
          <span className="tm__ic" style={{ background: t.bg, color: t.fg }}>{t.icon}</span>
          <span className="tm__x">
            <b>{t.name}</b>
            <small className={`tm__s tm__s--${t.status.tone}`}>{t.status.text}</small>
          </span>
        </button>
      ))}
    </div>
  );
}

// color: { fg } the section's own colour (the pill is solid in it). The explanation stands quiet under the pill and is
// at full strength when the mouse is over it.
export function SectionHeading({ title, extra, count, text, color, folded, onToggle, children }) {
  return (
    <div className="sx" style={{ '--sc-fg': color }}>
      <button type="button" className={`sx__h${folded ? ' is-coll' : ''}`} aria-expanded={!folded} onClick={onToggle}>
        <b>{title}</b>
        {extra && <em className="sx__x">{extra}</em>}
        <span className="sx__n">{count}</span>
        <svg className="sx__cv" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
        {text && !folded && <small>{text}</small>}
      </button>
      {!folded && <div className="sx__b">{children}</div>}
    </div>
  );
}

// One card. tone: bad, warn, ok or other. rows is the markup of the visible rows; the footer shows "Vis alle N →"
// when some are hidden, and "Træf beslutning" on every card.
export function StatusCard({ tone, title, pill, selected, folded, onFold, onActivate, rows, total, hidden, onShowAll, decideLabel, onDecide, empty, markAll, it }) {
  return (
    <section className={`wqc wqc--solo wqc--${tone}${selected ? ' is-sel' : ''}${folded ? ' is-coll' : ''}`} onClick={onActivate}>
      <header className="wqc__h" onClick={(e) => { e.stopPropagation(); onActivate(); onFold(); }}>
        <h3>{title}</h3>
        <span className={`wqc__pill wqc__pill--${pill.tone}`}>{pill.text}</span>
        <button type="button" className="wqc__chev" aria-label={folded ? 'Åbn' : 'Luk'} aria-expanded={!folded} onClick={(e) => { e.stopPropagation(); onFold(); }}>{CHEVRON}</button>
      </header>
      {!folded && (
        <>
          <div className="wqc__rows">{rows}{empty && <p className="sx__none">{empty}</p>}</div>
          <footer className="wqc__f">
            <span className="wqc__lft">
              {markAll && (
                <button type="button" className="wqc__ma" data-tip={markAll.label} aria-label={markAll.label} onClick={(e) => { e.stopPropagation(); markAll.onClick(); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8 12l3 3 5-6" /></svg>
                </button>
              )}
              {hidden > 0 && <button type="button" className="wqc__more" onClick={(e) => { e.stopPropagation(); onShowAll(); }}>Vis alle {total} →</button>}
            </span>
            {selected && (
              <span className="wqc__acts">
                {it && (
                  <button type="button" className={`itl-btn${it.on ? ' is-on' : ''}`} aria-pressed={it.on} disabled={it.disabled} title={it.on ? 'På IT-opgaver. Klik for at fjerne' : 'Tilføj til IT-opgaver'} onClick={(e) => { e.stopPropagation(); it.onToggle(); }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{it.on ? <path d="M5 12l5 5L20 7" /> : <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />}</svg>
                    {it.label}
                  </button>
                )}
                {onDecide && <button type="button" className="wqc__btn" onClick={(e) => { e.stopPropagation(); onDecide(); }}>{CLIPBOARD}{decideLabel}</button>}
              </span>
            )}
          </footer>
        </>
      )}
    </section>
  );
}

// One folded card of the side panel: a grey title strip with a count, and either a one-line summary or the whole
// content once the strip is pressed.
// A folded group of the side panel: the heading and the count, nothing else. (One component for every panel.)
export function PanelCard({ title, count, open, onToggle, tone, children }) {
  return (
    <section className={`pn-card${open ? ' is-open' : ''}${tone ? ` pn-card--${tone}` : ''}`}>
      <button type="button" className="pn-card__h" aria-expanded={open} onClick={onToggle}>
        <span>{title}</span>
        {count !== null && count !== undefined && <b>{count}</b>}
        <svg className="pn-card__ch" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && <div className="pn-card__body">{children}</div>}
    </section>
  );
}

// The box at the top of every side panel, and the Anbefaling under it: one template for Gennemgang, Compliance and Konfiguration.
// tone: bad (red), warn (orange) or ok (green); one tone per panel, the same that colours the dot by the title.
const ICON_BANG = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.6M12 16.4v.01" /></svg>;
const ICON_CHECK = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M8 12.5l3 3 5-6" /></svg>;
const ICON_ARROW = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
export const TONE_OF_DOT = { r: 'bad', o: 'warn', g: 'ok' };
export function PanelAlert({ tone = 'bad', small, children }) {
  return (
    <div className={`pn-adv${tone === 'warn' ? ' pn-adv--warn' : tone === 'ok' ? ' pn-adv--ok' : ''}`}>
      <span className="pn-adv__i">{tone === 'ok' ? ICON_CHECK : ICON_BANG}</span>
      <div className="pn-adv__t"><span>{children}</span>{small && <small>{small}</small>}</div>
    </div>
  );
}
export function PanelRec({ title = 'Anbefaling', children }) {
  return (
    <div className="pn-rec">
      <span className="pn-rec__i">{ICON_ARROW}</span>
      <div className="pn-rec__t"><h6>{title}</h6><p>{children}</p></div>
    </div>
  );
}

// A list of named things inside a card (permissions, members, policies): the name, a small tag, and, when there is one, a
// short explanation behind a chevron. Long lists show the first rows and "Vis alle".
export function PanelItems({ items, limit = 25 }) {
  const [openRows, setOpenRows] = useState(() => new Set());
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, limit);
  return (
    <div className="pi">
      {shown.map((it, i) => {
        const can = !!it.desc;
        const o = openRows.has(i);
        return (
          <div className={`pi__r${o ? ' is-open' : ''}`} key={`${it.name}-${i}`}>
            <button type="button" className="pi__h" disabled={!can} aria-expanded={can ? o : undefined} onClick={() => setOpenRows((prev) => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; })}>
              <b className={it.mono ? 'pi__m' : undefined}>{it.name}</b>
              {it.tag && <span>{it.tag}</span>}
              {can ? <svg className="pi__cv" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg> : <i className="pi__nc" />}
            </button>
            {can && o && <p>{it.desc}</p>}
          </div>
        );
      })}
      {!all && items.length > limit && <button type="button" className="pi__more" onClick={() => setAll(true)}>Vis alle {items.length} →</button>}
    </div>
  );
}

// tiles: [[label, value, tone]] tone r, o, g or n. warn: { text, cons } or null (the red Advarsel box). note: { tone: bad|warn|ok, title, text } or null.
// cards: [{ key, title, count, summary, body, open, tone, pre }] (open: starts unfolded; tone: bad, warn or ok tints the card; pre: stands under the Anbefaling, above Detaljer, and is not part of "Udvid alle"). decision: { tag, tagKey, meta, label, onOpen }.
export function OnePagePanel({ title, sub, banner, tiles, warn, note, cards, decision }) {
  // "Hvad er det" (pre) is always closed to begin with, also when "Udvid alle" is remembered.
  const [openCards, setOpenCards] = useState(() => new Set(readExpandAll() ? cards.filter((c) => !c.pre || c.open).map((c) => c.key) : cards.filter((c) => c.open).map((c) => c.key)));
  // Another object (other cards) while the choice is "all open": open them all too.
  const cardSig = cards.map((c) => c.key).join('|');
  useEffect(() => { if (readExpandAll()) setOpenCards(new Set(cards.filter((c) => !c.pre || c.open).map((c) => c.key))); }, [cardSig]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (key) => setOpenCards((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  const preCards = cards.filter((c) => c.pre);
  const detailCards = cards.filter((c) => !c.pre);
  const allOpen = detailCards.length > 0 && detailCards.every((c) => openCards.has(c.key));
  return (
    <div className="pn">
      {banner}
      <div className="pn-h pn-h--dot">
        <div className="pn-h__t">
          <h3 title={title}>{title}</h3>
          {sub && <p>{sub}</p>}
        </div>
        {tiles[0] && <span className={`pn-dot pn-dot--${tiles[0][2]}`} title={tiles[0][1]} aria-label={tiles[0][1]} />}
      </div>
      <div className="pn-body">
        {warn && <PanelAlert tone={TONE_OF_DOT[tiles[0]?.[2]] || 'bad'} small={warn.cons}>{warn.text}</PanelAlert>}
        {note && /^Anbefaling/.test(note.title) && <PanelRec title={note.title}>{note.text}</PanelRec>}
        {note && !/^Anbefaling/.test(note.title) && (
          <div className={`pn-risk${note.tone === 'warn' ? ' pn-risk--warn' : note.tone === 'ok' ? ' pn-risk--ok' : ''}`}>
            <div className="pn-risk__h">{note.title}</div>
            <p>{note.text}</p>
          </div>
        )}
        {preCards.map((c) => (
          <PanelCard key={c.key} title={c.title} count={c.count} summary={c.summary} open={openCards.has(c.key)} onToggle={() => toggle(c.key)} tone={c.tone || 'what'}>{c.body}</PanelCard>
        ))}
        {detailCards.length > 0 && (
          <div className={`pn-dh${preCards.length ? ' pn-dh--after' : ''}`}>
            <span>Detaljer</span>
            {detailCards.length > 1 && <ExpandAll allOpen={allOpen} onClick={() => { writeExpandAll(!allOpen); setOpenCards(allOpen ? new Set(preCards.filter((c) => openCards.has(c.key)).map((c) => c.key)) : new Set([...preCards.filter((c) => openCards.has(c.key)).map((c) => c.key), ...detailCards.map((c) => c.key)])); }} />}
          </div>
        )}
        {detailCards.map((c) => (
          <PanelCard key={c.key} title={c.title} count={c.count} summary={c.summary} open={openCards.has(c.key)} onToggle={() => toggle(c.key)} tone={c.tone}>{c.body}</PanelCard>
        ))}
      </div>
      {/* The decision is made on the card; the panel only shows one that has been made. */}
      {decision.tag && (
        <div className="pn-foot">
          <div className="pn-foot__r"><span>Beslutning</span></div>
          <div className="pn-foot__s">
            <DecisionTag badge={getStatusBadge({ decisionType: decision.tagKey })} /><small>{decision.meta}</small>
          </div>
        </div>
      )}
    </div>
  );
}
